import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";

/**
 * Terry, the landing page's guide. He runs in on a first visit, offers to show
 * you around, walks the page section by section with a spotlight and a speech
 * bubble, then parks himself bottom-right as a small helper you can tap.
 *
 * The tour anchors are ids on Landing. A step whose element is missing is
 * skipped rather than stranding Terry mid-air, so the page can drop a section
 * without breaking him.
 *
 * Everything here is decoration around content that stands on its own — the
 * tour is offered, never forced, Escape leaves at any point, and a visitor who
 * has already seen it gets the small helper straight away.
 */

const SEEN_KEY = "talesWithTerryTourSeen";
const NUDGE_KEY = "talesWithTerryHelperFound";

const POSES = {
    run: "run", wave: "wave", hero: "hero", point: "point",
    reading: "reading", curious: "curious", writing: "writing",
    excited: "heart", thinking: "thinking",
};

// Adapted from the design boards to the sections this landing page actually
// has — the boards toured an inline creation form, which lives behind sign-in
// here, so the tour walks what a signed-out visitor can really see.
const STEPS = [
    { sel: "#hero-cta", pose: "point", text: "Let's make a magical story together!" },
    { sel: "#how", pose: "wave", text: "Four gentle steps — that's the whole thing." },
    { sel: "#themes", pose: "thinking", text: "Where should our adventure begin?" },
    { sel: "#peek", pose: "reading", text: "And this is where the magic happens!" },
    { sel: "#pricing", pose: "excited", text: "Read it tonight, or hold it in your hands." },
    { sel: "#final-cta", pose: "point", text: "Ready to create your first adventure?" },
];

const prefersReduced = () => {
    try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch { return false; }
};
const hasSeen = () => {
    try { return localStorage.getItem(SEEN_KEY) === "1"; } catch { return false; }
};
const remember = () => {
    try { localStorage.setItem(SEEN_KEY, "1"); } catch { /* private mode — he just says hello again */ }
};

const TerryGuide = forwardRef(function TerryGuide({ onCreate }, ref) {
    const small = typeof window !== "undefined" && window.innerWidth < 720;
    const firstVisit = useRef(!hasSeen());
    const reduced = useRef(prefersReduced());

    const [splash, setSplash] = useState(!reduced.current);
    const [phase, setPhase] = useState("boot");
    const [step, setStep] = useState(0);
    const [hl, setHl] = useState(null);
    const [t, setT] = useState({
        x: -140, y: 0, h: 54, pose: "run", dur: "0ms", anim: "none", opacity: 0, side: 1,
    });

    const timers = useRef([]);
    const later = useCallback((fn, ms) => {
        const id = setTimeout(fn, ms);
        timers.current.push(id);
        return id;
    }, []);

    const toMini = useCallback(() => {
        setHl(null);
        setPhase("mini");
        setT((s) => ({
            ...s, pose: "wave", h: small ? 56 : 68,
            x: window.innerWidth - (small ? 52 : 64),
            y: window.innerHeight - 26,
            dur: "620ms", anim: "terryIdle 5s ease-in-out infinite", opacity: 1,
        }));
    }, [small]);

    /** Park Terry beside a step's element and draw the spotlight. The index is
        explicit: goStep defers this past setStep, so reading `step` from the
        closure would pin him to the step he just left. */
    const pin = useCallback((i = step) => {
        const s = STEPS[i];
        const el = s && document.querySelector(s.sel);
        if (!el) return;
        const r = el.getBoundingClientRect();
        const vw = window.innerWidth, vh = window.innerHeight;
        const h = small ? 96 : 128;
        let x, side;
        if (vw - r.right > 170) { x = Math.min(vw - 70, r.right + 80); side = 1; }
        else if (r.left > 170) { x = Math.max(70, r.left - 80); side = -1; }
        else { x = Math.min(vw - 70, r.right - 30); side = 1; }
        const y = Math.max(h + 130, Math.min(vh - 18, r.bottom + 26));
        setHl({ x: r.left - 10, y: r.top - 10, w: r.width + 20, h: r.height + 20 });
        setT({ x, y, h, pose: s.pose, dur: "760ms", side, opacity: 1,
               anim: "terryIdle 4.4s ease-in-out infinite" });
    }, [step, small]);

    /** Scroll the step into view, then pin once the scroll has settled. */
    const goStep = useCallback((i) => {
        const s = STEPS[i];
        const el = s && document.querySelector(s.sel);
        if (!el) return;
        const r = el.getBoundingClientRect();
        const target = r.top + window.scrollY - Math.max(90, (window.innerHeight - r.height) / 2);
        window.scrollTo({ top: Math.max(0, target), behavior: reduced.current ? "auto" : "smooth" });
        later(() => pin(i), reduced.current ? 60 : 620);
    }, [later, pin]);

    // Terry's entrance. He runs in on every load; only a visitor who asked for
    // less motion skips straight to finding him waiting.
    useEffect(() => {
        if (splash) return undefined;
        if (reduced.current) {
            if (!firstVisit.current) { toMini(); return undefined; }
            setPhase("greet");
            setT({ x: window.innerWidth - (small ? 80 : 130), y: window.innerHeight - 30,
                   h: small ? 96 : 130, pose: "wave", dur: "400ms", anim: "none", opacity: 1, side: 1 });
            return undefined;
        }
        setT({ x: -140, y: window.innerHeight - 30, h: small ? 62 : 88,
               pose: "run", dur: "0ms", anim: "none", opacity: 1, side: 1 });
        const a = setTimeout(() => {
            setPhase("running");
            setT((s) => ({ ...s, x: window.innerWidth - (small ? 86 : 140), dur: "4200ms",
                           anim: "terryRunCycle 300ms ease-in-out infinite" }));
        }, 120);
        const b = setTimeout(() => {
            setPhase("arrive");
            setT((s) => ({ ...s, pose: "wave", h: small ? 104 : 150,
                           y: window.innerHeight - 24, dur: "520ms",
                           anim: "terryPop 620ms cubic-bezier(.34,1.56,.64,1) both" }));
        }, 4420);
        const c = setTimeout(() => {
            if (!firstVisit.current) { toMini(); return; }
            setPhase("greet");
            setT((s) => ({ ...s, anim: "terryIdle 4.4s ease-in-out infinite" }));
        }, 5060);
        return () => { clearTimeout(a); clearTimeout(b); clearTimeout(c); };
    }, [splash, small, toMini]);

    // Keep him glued to his element while the page moves under him.
    useEffect(() => {
        if (phase !== "tour") return undefined;
        const onMove = () => pin();
        window.addEventListener("scroll", onMove, { passive: true });
        window.addEventListener("resize", onMove);
        return () => {
            window.removeEventListener("scroll", onMove);
            window.removeEventListener("resize", onMove);
        };
    }, [phase, pin]);

    const skip = useCallback(() => { remember(); toMini(); }, [toMini]);

    const next = useCallback(() => {
        const n = step + 1;
        if (n >= STEPS.length) { skip(); return; }
        setStep(n);
        goStep(n);
    }, [step, goStep, skip]);

    const prev = useCallback(() => {
        const n = Math.max(0, step - 1);
        setStep(n);
        goStep(n);
    }, [step, goStep]);

    // Escape always gets you out; the arrows walk the tour once it is running.
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape" && (phase === "greet" || phase === "tour" || phase === "miniOpen")) skip();
            if (phase !== "tour") return;
            if (e.key === "ArrowRight") next();
            if (e.key === "ArrowLeft") prev();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [phase, skip, next, prev]);

    useEffect(() => () => timers.current.forEach(clearTimeout), []);

    // One Terry at a time.
    useEffect(() => {
        const busy = splash || ["boot", "running", "arrive", "greet", "tour"].includes(phase);
        document.body.dataset.terryBusy = busy ? "1" : "0";
        return () => { delete document.body.dataset.terryBusy; };
    }, [splash, phase]);

    // He introduces himself once, so the tap is discoverable without dressing
    // him up as a button. Once only — a mascot that keeps interrupting is worse
    // than one nobody taps.
    const nudged = useRef(false);
    useEffect(() => {
        if (phase !== "mini" || nudged.current) return undefined;
        nudged.current = true;
        // Once per browser, not once per reload — a helper that reintroduces
        // itself every visit is nagging, and by then they know he is there.
        try {
            if (localStorage.getItem(NUDGE_KEY) === "1") return undefined;
            localStorage.setItem(NUDGE_KEY, "1");
        } catch { /* private mode — he says hello again, no harm */ }
        // Via `later`, not raw timeouts: showing the bubble changes `phase`,
        // which re-runs this effect, and a local cleanup would cancel its own
        // auto-hide. These are cleared on unmount instead. The functional
        // updates make both no-ops if the visitor has moved on in the meantime.
        later(() => setPhase((p) => (p === "mini" ? "miniOpen" : p)), 2600);
        later(() => setPhase((p) => (p === "miniOpen" ? "mini" : p)), 8200);
        return undefined;
    }, [phase, later]);

    const accept = () => { setPhase("tour"); setStep(0); goStep(0); };
    const replay = () => {
        timers.current.forEach(clearTimeout);
        timers.current = [];
        window.scrollTo({ top: 0, behavior: "smooth" });
        setStep(0);
        setPhase("greet");
        setT({ x: window.innerWidth - (small ? 80 : 130), y: window.innerHeight - 30,
               h: small ? 104 : 150, pose: "wave", dur: "520ms", opacity: 1, side: 1,
               anim: "terryIdle 4.4s ease-in-out infinite" });
    };
    const createFromBubble = () => { remember(); toMini(); onCreate?.(); };

    // The footer's "Meet Terry again" reaches in here.
    useImperativeHandle(ref, () => ({ replay }));

    const last = step === STEPS.length - 1;
    const bubbleText =
        phase === "greet" ? "Hi! I'm Terry 👋\nWant me to show you around?"
        : phase === "tour" ? STEPS[step].text
        : phase === "miniOpen" ? "Need a little help? 👋"
        : "";
    const bubbleOn = phase === "greet" || phase === "tour" || phase === "miniOpen";
    const bias = phase === "tour" ? (t.side || 1) * 74 : 0;
    const bx = Math.max(160, Math.min((typeof window !== "undefined" ? window.innerWidth : 1200) - 160, t.x + bias));
    const by = t.y - t.h - 14;

    return (
        <>
            {splash && <Splash quick={!firstVisit.current} onDone={() => setSplash(false)} />}

            {/* Spotlight — a dashed ring around the step, with the rest of the
                page dimmed by one very large shadow. */}
            <div
                aria-hidden
                className="pointer-events-none fixed left-0 top-0 z-[55] rounded-[26px] border-[3px] border-dashed border-bubblegum"
                style={{
                    transition: "transform 620ms cubic-bezier(.22,1,.36,1), width 620ms ease, height 620ms ease, opacity 300ms ease",
                    transform: `translate(${hl ? hl.x : 0}px, ${hl ? hl.y : 0}px)`,
                    width: hl ? hl.w : 0,
                    height: hl ? hl.h : 0,
                    opacity: hl && phase === "tour" ? 1 : 0,
                    boxShadow: phase === "tour" ? "0 0 0 9999px rgba(45,27,78,.34)" : "0 0 0 rgba(45,27,78,0)",
                }}
            />

            {/* Terry himself. Clickable only once he is the small helper — mid
                tour he must not sit between you and a button. */}
            <div className="fixed left-0 top-0 z-[60] w-0 h-0">
                <div
                    className="absolute left-0 top-0"
                    style={{
                        opacity: t.opacity,
                        pointerEvents: phase === "mini" || phase === "miniOpen" ? "auto" : "none",
                        transform: `translate(${t.x}px, ${t.y}px) translate(-50%, -100%)`,
                        transition: `transform ${t.dur} ${phase === "running" ? "linear" : "cubic-bezier(.22,1,.36,1)"}, opacity 300ms ease`,
                    }}
                >
                    {phase === "running" && <Dust />}
                    <img
                        src={`/terry/terry-${POSES[t.pose]}.png`}
                        alt=""
                        draggable={false}
                        onClick={() => {
                            if (phase === "mini") setPhase("miniOpen");
                            else if (phase === "miniOpen") setPhase("mini");
                        }}
                        role={phase === "mini" || phase === "miniOpen" ? "button" : undefined}
                        tabIndex={phase === "mini" || phase === "miniOpen" ? 0 : -1}
                        aria-label={phase === "mini" || phase === "miniOpen" ? "Terry, your story guide — open for help" : undefined}
                        aria-hidden={phase === "mini" || phase === "miniOpen" ? undefined : true}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setPhase(phase === "mini" ? "miniOpen" : "mini");
                            }
                        }}
                        className="relative block w-auto max-w-none cursor-pointer origin-bottom drop-shadow-[0_14px_20px_rgba(45,27,78,0.25)]
                                   hover:brightness-105 focus-visible:outline focus-visible:outline-2
                                   focus-visible:outline-offset-4 focus-visible:outline-bubblegum"
                        style={{
                            height: t.h,
                            animation: t.anim,
                            transition: "height 520ms cubic-bezier(.34,1.56,.64,1)",
                        }}
                        data-testid="terry-guide"
                    />
                </div>
            </div>

            {/* Speech bubble */}
            <div className="fixed left-0 top-0 z-[61] w-0 h-0">
                <div
                    className="absolute left-0 top-0 w-[306px] max-w-[86vw]"
                    style={{
                        transition: "transform 620ms cubic-bezier(.22,1,.36,1), opacity 320ms ease",
                        opacity: bubbleOn ? 1 : 0,
                        pointerEvents: bubbleOn ? "auto" : "none",
                        transform: `translate(${bx}px, ${by}px) translate(-50%, -100%)`,
                    }}
                    role={bubbleOn ? "dialog" : undefined}
                    aria-label="Terry"
                    aria-hidden={bubbleOn ? undefined : true}
                >
                    <div className="card-cloud p-5 pb-4" data-testid="terry-bubble">
                        <div className="font-display text-[17px] leading-snug whitespace-pre-line text-plum">{bubbleText}</div>

                        {phase === "greet" && (
                            <div className="flex gap-2 mt-3.5">
                                <button onClick={accept} className="btn-sticker !py-2.5 !px-4 text-sm" data-testid="terry-tour-accept">
                                    Yes, show me!
                                </button>
                                <button onClick={skip}
                                    className="rounded-full px-3.5 py-2.5 text-sm text-plum/60 bg-paper border-2 border-plum/10">
                                    No, thanks
                                </button>
                            </div>
                        )}

                        {phase === "tour" && !last && (
                            <>
                                <div className="flex items-center gap-2 mt-3.5">
                                    <button onClick={prev}
                                        className="rounded-full px-3 py-2.5 text-sm text-plum/60 bg-paper border-2 border-plum/10">
                                        Back
                                    </button>
                                    <button onClick={next} className="btn-sticker !py-2.5 !px-[18px] text-sm" data-testid="terry-tour-next">
                                        Next
                                    </button>
                                    <button onClick={skip} className="ml-auto text-[13px] text-plum/45">Skip tour</button>
                                </div>
                                <Dots step={step} />
                            </>
                        )}

                        {phase === "tour" && last && (
                            <>
                                <div className="flex items-center gap-2 mt-3.5">
                                    <button onClick={createFromBubble} className="btn-sticker !py-2.5 !px-[18px] text-sm">
                                        Create My Story
                                    </button>
                                    <button onClick={skip}
                                        className="rounded-full px-3.5 py-2.5 text-sm text-plum/60 bg-paper border-2 border-plum/10">
                                        Finish tour
                                    </button>
                                </div>
                                <Dots step={step} />
                            </>
                        )}

                        {phase === "miniOpen" && (
                            <div className="flex flex-col gap-2 mt-3.5">
                                <button onClick={replay} className="btn-sticker !py-2.5 text-sm">Show me around</button>
                                <button onClick={createFromBubble} className="btn-sticker sky !py-2.5 text-sm">Create a story</button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
});

export default TerryGuide;

const Dots = ({ step }) => (
    <div className="flex gap-[5px] mt-3.5" aria-hidden>
        {STEPS.map((s, i) => (
            <div key={s.sel} className={`h-[5px] flex-1 rounded-full ${i <= step ? "bg-bubblegum" : "bg-plum/10"}`} />
        ))}
    </div>
);

/** The puff of dust behind Terry while he is running. */
const Dust = () => (
    <div className="absolute bottom-0.5 right-[78%] w-[90px] h-[34px] pointer-events-none" aria-hidden>
        <div className="absolute right-1 bottom-0.5 w-4 h-4 rounded-full bg-white/95 anim-dust" />
        <div className="absolute right-5 bottom-2 w-[11px] h-[11px] rounded-full bg-white/85 anim-dust" style={{ animationDelay: ".22s" }} />
        <div className="absolute right-9 bottom-0 w-2 h-2 rounded-full bg-white/75 anim-dust" style={{ animationDelay: ".44s" }} />
    </div>
);

/**
 * First-visit splash. Terry runs the width of a progress bar while the page
 * settles behind it. Returning visitors never see this — a splash you sit
 * through twice stops being charming.
 */
function Splash({ onDone, quick }) {
    const [pct, setPct] = useState(8);
    const [fade, setFade] = useState(false);

    useEffect(() => {
        const a = setTimeout(() => setPct(62), quick ? 90 : 250);
        const b = setTimeout(() => setPct(100), quick ? 340 : 1700);
        const c = setTimeout(() => setFade(true), quick ? 460 : 2100);
        const d = setTimeout(onDone, quick ? 760 : 2480);
        return () => [a, b, c, d].forEach(clearTimeout);
    }, [onDone, quick]);

    return (
        <div
            className="fixed inset-0 z-[80] flex flex-col items-center justify-center gap-7 transition-opacity duration-500"
            style={{
                opacity: fade ? 0 : 1,
                background: "linear-gradient(#FFE9B8, #FFF7E2 60%, #FFFBF5)",
            }}
            role="status"
            aria-label="Loading"
        >
            <img src="/terry/wordmark.png" alt="Tales with Terry" className="h-[52px] sm:h-[66px] w-auto" />
            <div className="relative w-[min(680px,82vw)] h-[110px] overflow-hidden">
                <div className="absolute left-0 bottom-5 anim-terry-dash">
                    <Dust />
                    <img src="/terry/terry-run.png" alt="" aria-hidden draggable={false}
                         className="block h-[84px] w-auto max-w-none origin-bottom anim-terry-run drop-shadow-[0_12px_18px_rgba(45,27,78,0.2)]" />
                </div>
                <div className="absolute left-0 right-0 bottom-0 h-2 rounded-full bg-plum/[0.07] overflow-hidden">
                    <div className="h-full rounded-full bg-rainbow-soft"
                         style={{ width: `${pct}%`, transition: "width 600ms ease-out" }} />
                </div>
            </div>
            <div className="script-note text-[28px]">Terry is on his way…</div>
        </div>
    );
}
